import { useChartTimeNavigation } from './ChartTimeNavigation'
import { memo, useMemo, useRef, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { format as chartFormat, type ECharts, type EChartsOption } from 'echarts'
import type { Time } from 'lightweight-charts'
import type { ScreenerCandle, ScreenerOIDataPoint, ScreenerTradePriceCandle } from '@/types'
import { buildPricePlot, pricePointAtTime } from './priceChartData'
import { ChartRuler, type OIRulerSeries, type RulerExchangeSeries } from './ChartRuler'
import { ChartCollapseButton } from './ChartCollapseButton'
import { ChartLevelAlerts, chartAlertMarkLine, type ChartAlertControls } from './ChartLevelAlerts'
import { useChartAlertDrag } from './useChartAlertDrag'
import { useVisibleChartScale } from './useVisibleChartScale'
import { useChartResize } from './useChartResize'
import { chartTooltipTime, useChartCrosshair } from './useChartCrosshair'
import {
  formatAge,
  formatUtc,
  formatUtcAxis,
} from './chartUtils'

interface PriceLineChartProps {
  oiData: ScreenerOIDataPoint[]
  candles: ScreenerCandle[]
  tradeHistory?: ScreenerTradePriceCandle[]
  candleSource?: string
  candleError?: string | null
  quoteCurrency?: string | null
  currentPrice?: number | null
  candleInterval?: string
  symbol: string
  exchange: string
  interval: string
  chartGroup: string
  onCrosshairMove: (time: Time | null) => void
  syncedTime: Time | null
  height?: number
  alertControls?: ChartAlertControls
  market?: 'spot' | 'futures'
  rulerExchanges?: RulerExchangeSeries[]
  collapsed?: boolean
  onToggleCollapse?: () => void
}

function formatPrice(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: value >= 100 ? 2 : 4,
    maximumFractionDigits: value >= 1 ? 6 : 8,
  })
}

function PriceLineChartComponent({
  oiData,
  candles,
  tradeHistory,
  candleSource,
  candleError,
  quoteCurrency,
  currentPrice,
  candleInterval,
  symbol,
  exchange,
  interval,
  chartGroup,
  onCrosshairMove,
  syncedTime,
  height = 330,
  alertControls: savedAlertControls,
  market = 'futures',
  rulerExchanges,
  collapsed = false,
  onToggleCollapse,
}: PriceLineChartProps) {
  const chartRef = useRef<ReactECharts>(null)
  const [rulerChart, setRulerChart] = useState<ECharts | null>(null)
  const [preferredMode, setPreferredMode] = useState<'line' | 'candles' | null>(null)
  const native = candleSource === 'trade_bars_v1'
  const mode = native ? preferredMode ?? 'candles' : 'line'
  const priceUnit = quoteCurrency || 'USD'
  const displayPrice = (value: number) => priceUnit === 'USD' || priceUnit === 'USDT'
    ? `$${formatPrice(value)}` : `${formatPrice(value)} ${priceUnit}`
  useChartResize(rulerChart)
  const timeNavigation = useChartTimeNavigation(rulerChart)

  const derived = useMemo(() => buildPricePlot({ oiData: market === 'futures' ? oiData : [], candles, tradeHistory: tradeHistory ?? [], native }),
    [oiData, candles, tradeHistory, native, market])
  const alertMarket = derived.source === 'OI aligned' ? 'futures' : market
  const alertControls = useChartAlertDrag(rulerChart, savedAlertControls, {
    symbol, metric: 'price', market: alertMarket, exchanges: [exchange],
  })

  const scaleSeries = mode === 'candles' ? derived.candleScaleSeries : derived.scaleSeries
  const alertSeries = useMemo(() => [{ exchange, points: derived.series.filter((point): point is [number, number] => point[1] != null) }], [exchange, derived.series])
  const { fitVisibleScale, onChartReady } = useVisibleChartScale(scaleSeries, `${symbol}:${exchange}:${interval}:${mode}`, true)

  const rulerSeries = useMemo<OIRulerSeries[]>(() => [{
    id: exchange,
    label: exchange,
    color: '#2dd4bf',
    points: derived.points.flatMap((point) => point.price == null ? [] : [{ timestamp: point.timestamp, value: point.price }]),
    oiData,
    candles,
    currentPrice,
  }], [exchange, derived.points, oiData, candles, currentPrice])

  const selected = useMemo(() => {
    if (derived.points.length === 0) return { point: null, age: null }
    if (syncedTime == null) {
      const latest = derived.points[derived.points.length - 1]
      return { point: latest.price == null ? null : latest, age: null }
    }
    const targetMs = Number(syncedTime) * 1000
    const point = pricePointAtTime(derived, targetMs)
    if (!point) return { point: null, age: null }
    return { point, age: Math.max(0, Math.floor(Number(syncedTime) - point.timestamp)) }
  }, [derived, syncedTime])

  const option = useMemo<EChartsOption>(() => ({
    animation: false,
    useUTC: true,
    backgroundColor: 'transparent',
    grid: { left: 10, right: 76, top: 10, bottom: 34, containLabel: true },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross', lineStyle: { color: '#64748b', type: 'dashed' } },
      backgroundColor: 'rgba(5, 10, 18, 0.96)',
      borderColor: '#334155',
      textStyle: { color: '#e2e8f0', fontSize: 11 },
      formatter: (raw: any) => {
        const entries = Array.isArray(raw) ? raw : [raw]
        const axisMs = chartTooltipTime(chartRef.current?.getEchartsInstance(), Number(entries[0]?.axisValue ?? 0))
        const point = pricePointAtTime(derived, axisMs)
        if (!point || point.price == null) return `<div>${formatUtc(axisMs, interval === '5s')}</div><div>Price: N/A · no recorded bucket</div>`
        const age = Math.max(0, Math.floor((axisMs - point.timestamp * 1000) / 1000))
        const candle = point.candle
        const field = (value: number | null) => value == null ? 'N/A' : formatPrice(value)
        return `
          <div style="color:#94a3b8;margin-bottom:5px">${formatUtc(axisMs, interval === '5s')}</div>
          <div>${chartFormat.encodeHTML(exchange.toUpperCase())} Price: <b style="color:#2dd4bf">${chartFormat.encodeHTML(displayPrice(point.price))}</b></div>
          ${candle ? `<div>O ${field(candle.open)} · H ${field(candle.high)} · L ${field(candle.low)} · C ${field(candle.close)}</div>` : ''}
          ${candle?.stale ? '<div style="color:#f59e0b;font-size:10px">Stale · held during observed feed coverage</div>' : ''}
          ${candle?.complete === false && !candle.stale ? '<div style="color:#f59e0b;font-size:10px">Recorded trades · collection completeness unverified</div>' : ''}
          ${age > 0 && !candle ? `<div style="color:#f59e0b;font-size:10px">Last sample ${formatAge(age)}</div>` : ''}
        `
      },
    },
    xAxis: {
      type: 'time',
      axisPointer: { snap: false },
      ...timeNavigation.axis,
      axisLine: { lineStyle: { color: '#253044' } },
      axisTick: { show: false },
      axisLabel: {
        color: '#64748b',
        fontSize: 10,
        hideOverlap: true,
        formatter: (value: number) => formatUtcAxis(value, interval),
      },
      splitLine: { show: true, lineStyle: { color: '#172033', type: 'dashed' } },
    },
    yAxis: {
      type: 'value',
      position: 'right',
      triggerEvent: true,
      scale: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#64748b',
        fontSize: 10,
        formatter: (value: number) => formatPrice(value),
      },
      splitLine: { lineStyle: { color: '#172033', type: 'dashed' } },
    },
    dataZoom: [
      {
        type: 'inside',
        id: 'screener-time',
        ...timeNavigation.zoom,
        xAxisIndex: [0],
        zoomOnMouseWheel: true,
        moveOnMouseWheel: false,
        moveOnMouseMove: true,
        filterMode: 'none',
      },
      {
        type: 'inside',
        yAxisIndex: [0],
        id: 'screener-price-scale',
        zoomOnMouseWheel: 'shift',
        moveOnMouseMove: 'alt',
        moveOnMouseWheel: false,
        filterMode: 'none',
      },
    ],
    series: [{
      // Keep the initialized model available to retained tooltips during lazy updates.
      id: 'price',
      name: 'Price',
      ...(mode === 'candles' ? {
        type: 'candlestick' as const,
        dimensions: ['timestamp', 'open', 'close', 'low', 'high'],
        encode: { x: 0, y: [1, 2, 3, 4] },
        barMaxWidth: 12, barMinWidth: 1,
        itemStyle: { color: '#2dd4bf', color0: '#fb7185', borderColor: '#2dd4bf', borderColor0: '#fb7185' },
        data: derived.candleSeries,
      } : {
      type: 'line' as const,
      step: native ? 'end' as const : undefined,
      showSymbol: derived.points.length === 1,
      smooth: false,
      connectNulls: false,
      lineStyle: { width: 1.5, color: '#2dd4bf' },
      itemStyle: { color: '#2dd4bf' },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(45, 212, 191, 0.16)' },
            { offset: 1, color: 'rgba(45, 212, 191, 0.01)' },
          ],
        },
      },
      data: derived.series,
      }),
      markLine: chartAlertMarkLine(alertControls, symbol, exchange, 'price', alertMarket),
    }],
  }), [derived, exchange, interval, alertControls, symbol, alertMarket, timeNavigation, mode, native, priceUnit])

  useChartCrosshair(rulerChart, chartGroup, onCrosshairMove, interval)

  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-800/90 bg-[#080d16] shadow-[0_18px_50px_rgba(0,0,0,0.22)]">
      <div className="relative z-10 flex min-h-12 flex-wrap items-center justify-between gap-2 px-3 py-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {onToggleCollapse && <ChartCollapseButton collapsed={collapsed} onToggle={onToggleCollapse} label="price chart" />}
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Price</span>
          <span className="text-xs font-semibold uppercase text-slate-200">{symbol} · {exchange}</span>
          <span className="rounded-full border border-slate-700/70 bg-slate-900/80 px-2 py-0.5 text-[9px] uppercase tracking-wide text-slate-400">
            {derived.source}{native && candleInterval ? ` · ${candleInterval}` : ''}
          </span>
          {native && <select aria-label="Price chart type" value={mode}
            onChange={(event) => setPreferredMode(event.target.value as 'line' | 'candles')}
            className="rounded border border-slate-700 bg-slate-900 px-1 py-0.5 text-[10px] text-slate-300">
            <option value="candles">Candles</option><option value="line">Line</option>
          </select>}
        </div>
        {selected.point && (
          <button type="button" onClick={fitVisibleScale}
            title="Click to fit price scale to the visible time range"
            aria-label="Fit price scale to visible time range"
            className="flex cursor-pointer items-center gap-2 rounded-md border border-teal-400/20 bg-teal-400/10 px-2.5 py-1 hover:border-teal-300/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-300">
            <span className="font-mono text-xs font-semibold text-teal-300">{displayPrice(selected.point.price!)}</span>
            {selected.age != null && selected.age > 0 && (
              <span className="text-[9px] text-amber-400">{formatAge(selected.age)}</span>
            )}
          </button>
        )}
      </div>
      <div className="relative" style={collapsed ? { display: 'none' } : undefined} aria-hidden={collapsed || undefined}>
        <ReactECharts key={mode} ref={chartRef} option={option} style={{ height: Math.max(120, height - 48), width: '100%' }} autoResize={false}
          onChartReady={(instance) => { instance.group = chartGroup; onChartReady(instance); setRulerChart(instance) }}
          replaceMerge={['series']} lazyUpdate />
        {!derived.points.some((point) => point.price != null) && <div role="status"
          className="pointer-events-none absolute inset-0 flex items-center justify-center px-4 text-center text-xs text-slate-500">
          {candleError || 'No recorded price history for this time range'}
        </div>}
        {derived.points.some((point) => point.price != null) && candleError && <div role="status"
          className="absolute left-3 top-0 text-[9px] text-amber-400">{candleError}</div>}
        {!collapsed && <ChartRuler chart={rulerChart} metric="price" series={rulerSeries} rulerExchanges={rulerExchanges} resetKey={`${symbol}:${exchange}:${interval}`}
          interval={interval} candleInterval={candleInterval} barInterval={native || derived.source === 'Candle close' ? candleInterval : interval} />}
        {!collapsed && <ChartLevelAlerts chart={rulerChart} metric="price" market={alertMarket} series={alertSeries} controls={alertControls} />}
      </div>
    </div>
  )
}

export const PriceLineChart = memo(PriceLineChartComponent)
